import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/axios'

export interface Annotation {
  id: string
  document_id: string
  page_number: number
  field_id: string | null
  bounding_box: { x: number; y: number; width: number; height: number } | null
  selected_text: string | null
  corrected_value: string | null
  annotation_note: string | null
  created_by: string
  created_at: string
}

export interface CreateAnnotationPayload {
  document_id: string
  page_number: number
  field_id?: string | null
  bounding_box?: { x: number; y: number; width: number; height: number } | null
  selected_text?: string | null
  corrected_value?: string | null
  annotation_note?: string | null
}

export function useAnnotations(documentId: string | null) {
  const queryClient = useQueryClient()

  const { data: annotations = [], isLoading } = useQuery({
    queryKey: ['annotations', documentId],
    queryFn: () =>
      api
        .get<Annotation[]>('/annotations', { params: { document_id: documentId } })
        .then((r) => r.data),
    enabled: !!documentId,
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateAnnotationPayload) =>
      api.post<Annotation>('/annotations', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['annotations', documentId] })
      // Refresh document list so annotation_count updates
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (annotationId: string) =>
      api.delete(`/annotations/${annotationId}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['annotations', documentId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })

  return {
    annotations,
    isLoading,
    createAnnotation: createMutation.mutateAsync,
    deleteAnnotation: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}
